import { useCallback, useEffect, useRef, useState } from "react";
import { useHotkeys, useHotkeysContext } from "react-hotkeys-hook";

import type { DataTableToolbarRef } from "@/components/common/data-table/data-table-toolbar";

import { hotkeys } from "@/core/hotkeys";

interface UseTableNavigationOptions {
  scope: string;
  rowCount: number;
  onRowFocus?: (rowIndex: number) => void;
  onRowSelect?: (rowIndex: number) => void;
  canGoPreviousPage?: boolean;
  canGoNextPage?: boolean;
  onPreviousPage?: () => void;
  onNextPage?: () => void;
  onRowHover?: (rowIndex: number) => void;
  toolbarRef?: React.RefObject<DataTableToolbarRef | null>;
}

export function useTableNavigation({
  scope,
  rowCount,
  onRowFocus,
  onRowSelect,
  canGoPreviousPage = false,
  canGoNextPage = false,
  onPreviousPage,
  onNextPage,
  onRowHover,
  toolbarRef: externalToolbarRef,
}: UseTableNavigationOptions) {
  const { enableScope, disableScope } = useHotkeysContext();
  const [focusedRowIndex, setFocusedRowIndex] = useState<number>(-1); // Start with no row focused
  const tableRef = useRef<HTMLTableElement>(null);
  const internalToolbarRef = useRef<DataTableToolbarRef>(null);
  const hasUsedArrowNavigation = useRef<boolean>(false);

  // Use external toolbar ref if provided, otherwise use internal one
  const toolbarRef = externalToolbarRef ?? internalToolbarRef;

  useEffect(() => {
    enableScope(scope);
    return () => {
      disableScope(scope);
    };
  }, [enableScope, disableScope, scope]);

  const navigateUp = useCallback(() => {
    if (rowCount === 0 || focusedRowIndex <= 0) return;

    hasUsedArrowNavigation.current = true;
    const newIndex = focusedRowIndex - 1;
    setFocusedRowIndex(newIndex);
    onRowFocus?.(newIndex);

    if (newIndex >= 0 && tableRef.current) {
      const focusedRow = tableRef.current.querySelector(`tbody tr[data-row-index="${newIndex}"]`);
      focusedRow?.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
    }
  }, [focusedRowIndex, rowCount, onRowFocus]);

  const navigateDown = useCallback(() => {
    if (rowCount === 0) return;

    hasUsedArrowNavigation.current = true;

    if (focusedRowIndex === -1) {
      setFocusedRowIndex(0);
      onRowFocus?.(0);
      return;
    }

    if (focusedRowIndex >= rowCount - 1) return;

    const newIndex = focusedRowIndex + 1;
    setFocusedRowIndex(newIndex);
    onRowFocus?.(newIndex);

    if (newIndex >= 0 && tableRef.current) {
      const focusedRow = tableRef.current.querySelector(`tbody tr[data-row-index="${newIndex}"]`);
      focusedRow?.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
    }
  }, [focusedRowIndex, rowCount, onRowFocus]);

  const selectCurrentRow = useCallback(() => {
    if (focusedRowIndex >= 0 && focusedRowIndex < rowCount) {
      onRowSelect?.(focusedRowIndex);
    }
  }, [focusedRowIndex, rowCount, onRowSelect]);

  const handleRowHover = useCallback(
    (rowIndex: number) => {
      if (rowIndex >= 0 && rowIndex < rowCount) {
        setFocusedRowIndex(rowIndex);
        onRowHover?.(rowIndex);
      }
    },
    [rowCount, onRowHover],
  );

  useHotkeys(
    hotkeys.navigateUp,
    (event) => {
      event.preventDefault();
      navigateUp();
    },
    {
      scopes: [scope],
      preventDefault: true,
      enableOnFormTags: true,
    },
  );

  useHotkeys(
    hotkeys.navigateDown,
    (event) => {
      event.preventDefault();
      navigateDown();
    },
    {
      scopes: [scope],
      preventDefault: true,
      enableOnFormTags: true,
    },
  );

  useHotkeys(
    "enter",
    (event) => {
      const activeElement = document.activeElement;

      // Only handle Enter if we're not in a form input/select/textarea,
      // OR if we're specifically in the main search input
      const isInSearchInput = activeElement?.closest('[data-table-search="true"]');
      const isInFormInput =
        activeElement?.tagName === "INPUT" ||
        activeElement?.tagName === "SELECT" ||
        activeElement?.tagName === "TEXTAREA" ||
        activeElement?.getAttribute("role") === "combobox";

      // If we're in a form input that's NOT the search input, don't handle Enter
      if (isInFormInput && !isInSearchInput) {
        return;
      }

      // Only allow Enter to navigate/select rows if arrow navigation was used
      if (!hasUsedArrowNavigation.current) {
        return;
      }

      // If we're in the search input and no row is focused, select first row
      if (isInSearchInput && focusedRowIndex === -1 && rowCount > 0) {
        event.preventDefault();
        setFocusedRowIndex(0);
        onRowSelect?.(0);
        return;
      }

      // If a row is focused, select it
      if (focusedRowIndex >= 0) {
        event.preventDefault();
        selectCurrentRow();
      }
    },
    {
      scopes: [scope],
      preventDefault: false,
      enableOnFormTags: true,
    },
  );

  useHotkeys(
    hotkeys.previousPage,
    (event) => {
      if (canGoPreviousPage && onPreviousPage) {
        event.preventDefault();
        onPreviousPage();
        setFocusedRowIndex(-1);
        hasUsedArrowNavigation.current = false;
      }
    },
    {
      scopes: [scope],
      preventDefault: true,
      enableOnFormTags: true,
    },
  );

  useHotkeys(
    hotkeys.nextPage,
    (event) => {
      if (canGoNextPage && onNextPage) {
        event.preventDefault();
        onNextPage();
        setFocusedRowIndex(-1);
        hasUsedArrowNavigation.current = false;
      }
    },
    {
      scopes: [scope],
      preventDefault: true,
      enableOnFormTags: true,
    },
  );

  useEffect(() => {
    if (rowCount === 0) {
      setFocusedRowIndex(-1);
      hasUsedArrowNavigation.current = false;
    } else if (focusedRowIndex >= rowCount) {
      setFocusedRowIndex(-1);
      hasUsedArrowNavigation.current = false;
    }
  }, [rowCount, focusedRowIndex]);

  useHotkeys(
    hotkeys.focusSearch,
    (event) => {
      event.preventDefault();
      toolbarRef.current?.focusSearch();
    },
    {
      scopes: [scope],
      preventDefault: true,
    },
  );

  useHotkeys(
    hotkeys.clearSearch,
    (event) => {
      event.preventDefault();
      toolbarRef.current?.clearSearch();
    },
    {
      scopes: [scope],
      preventDefault: true,
      enableOnFormTags: true,
    },
  );

  useHotkeys(
    hotkeys.refreshTable,
    (event) => {
      event.preventDefault();
      toolbarRef.current?.refresh();
    },
    {
      scopes: [scope],
      preventDefault: true,
    },
  );

  useHotkeys(
    hotkeys.resetFilters,
    (event) => {
      event.preventDefault();
      toolbarRef.current?.resetFilters();
    },
    {
      scopes: [scope],
      preventDefault: true,
    },
  );

  return {
    toolbarRef,
    tableRef,
    focusedRowIndex,
    setFocusedRowIndex,
    navigateUp,
    navigateDown,
    selectCurrentRow,
    handleRowHover,
  };
}
